'use client';
import React, { useState } from 'react';
import dynamic from 'next/dynamic';

import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { useUIListener } from '@repo/ui/src/modules/UI/hooks/useUIListener';
import { useDelayedCB } from '@hooks/useDelayedCB';
import { ModalID, useDrawer, useModal } from '@repo/ui/src/modules/UI';
import { trackCart } from '@functions/monetate/cart';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import useBasket from '@hooks/basket/useBasket';
import Auth from '@components/Auth';
import useGoToCheckout from '@hooks/basket/useGoToCheckout';
import { useCustomer } from '@hooks/customer';
import { DrawerHeader } from '@repo/ui/src/modules/UI/components/Drawer/_DrawerHeader';
import { ErrorState } from '@repo/ui/src/components/Form';
import { classNames } from '@repo/ui/src/utils/classNames';
import { logCartViewed } from './helpers';
import CartBody from './_CartBody';
import CartFooter from './_CartFooter';
import CartPageView from './_CartPageView';

const CartRecommendations = dynamic(() => import('@components/Cart/CartRecommendations'));

const Cart = () => {
  const { inDrawer } = useDrawer();
  const { push: pushCheckoutLoader, close: closeCheckoutLoader } = useModal(ModalID.checkoutLoader);
  const { data: basket, isLoading } = useBasket();
  const { trigger: goToCheckout, error, isMutating } = useGoToCheckout();
  const { mutate: mutateCustomer } = useCustomer();
  const { t } = useTranslation();
  const { locale, push } = useCurrentUrl();
  const showRecommendations = locale === 'en-US';
  const [isAuthStep, setIsAuthStep] = useState(false);
  const [checkoutError, setCheckoutError] = React.useState(false);

  const navigateToCheckout = async (skipFetchCheck = false) => {
    pushCheckoutLoader();

    if (skipFetchCheck) {
      try {
        // Use window.location for hard navigation when session bridge failed
        window.location.href = `/${locale}/checkout`;
        return;
      } catch {
        closeCheckoutLoader();
        setCheckoutError(true);
        return;
      }
    }

    try {
      const result = await fetch(`/${locale}/checkout`);

      if (result.redirected) {
        // Try hard navigation as fallback when redirected
        try {
          window.location.href = `/${locale}/checkout`;
        } catch {
          closeCheckoutLoader();
          setCheckoutError(true);
        }
        return;
      }

      push(`/${locale}/checkout`);
    } catch {
      try {
        window.location.href = `/${locale}/checkout`;
      } catch {
        closeCheckoutLoader();
        setCheckoutError(true);
      }
    }
  };

  const outOfStockItems = error?.info;

  const skus = basket?.lineItems?.map((lineItem) => lineItem.sku);

  const disabled = outOfStockItems?.some((element: string) => skus?.includes(element));

  const { lineItems = [], totalItems = 0 } = basket || {};
  const isEmpty = !totalItems;

  const sendMonetate = React.useCallback(() => {
    trackCart(basket);
  }, [basket]);

  React.useEffect(() => {
    if (!inDrawer) {
      closeCheckoutLoader();
    }
  }, []);

  React.useEffect(() => {
    if (basket?.totalItems || basket?.giftCardTotal) {
      sendMonetate();
    }
  }, [lineItems, basket, sendMonetate]);

  const cb = useDelayedCB(
    (isOpen: boolean) => {
      if (basket) {
        logCartViewed(
          `${isOpen ? 'open' : 'close'} cart`,
          basket,
          lineItems,
          basket.recommendations,
        );
      }
      if (!isOpen) {
        setTimeout(() => setIsAuthStep(false), 500);
      }
    },
    React.useCallback(() => !isLoading, [isLoading]),
    { maxTries: 30 },
  );

  useUIListener(ModalID.cart, cb);

  const loggedOutTitle = t('cart.loggedOutTitle', undefined, { default: 'none' });
  const loggedOutSentence = t('cart.loggedOutSentence', undefined, { default: 'none' });

  if (!inDrawer) {
    return (
      <CartPageView
        basket={basket}
        locale={locale}
        isLoading={isLoading}
        outOfStockItems={error?.info}
      />
    );
  }

  return (
    <div className="tw-pb-28 tw-grid tw-grid-cols-1 tw-justify-center tw-min-h-screen tw-justify-items-stretch tw-grid-rows-[1fr_auto] md:tw-items-stretch md:tw-grid-areas-cartDrawer md:tw-grid-cols-[auto_auto] md:tw-pb-0">
      <div className="tw-full tw-flex tw-flex-col tw-flex-grow sm:tw-w-[600px] md:tw-grid-in-b">
        {isAuthStep ? (
          <Auth
            onSuccess={async () => {
              try {
                await mutateCustomer();

                // verification was done before auth step
                // this throws an error if there are out stock items skipping the next steps
                const goToCheckoutResult = await goToCheckout({ skipVerification: true });

                // If goToCheckout returned null (session bridge failed), use direct navigation
                const useDirectNavigation = goToCheckoutResult === null;

                navigateToCheckout(useDirectNavigation);
              } catch {
                try {
                  // Try direct window.location as ultimate fallback
                  window.location.href = `/${locale}/checkout`;
                } catch {
                  setCheckoutError(true);
                }
              }
            }}
            isCheckout
          />
        ) : (
          <>
            <DrawerHeader title={t('cart.title', undefined, { default: 'Your Cart' })} />
            <div className="tw-flex tw-justify-between tw-flex-col">
              <div className="tw-px-6 tw-mb-5 sm:tw-px-16 tw-mx-auto tw-max-w-3xl tw-w-full">
                {checkoutError && (
                  <ErrorState
                    text={t('cart.errorStateText', undefined, {
                      default: 'Something went wrong. Please try again.',
                    })}
                  />
                )}
                {loggedOutTitle !== 'none' && loggedOutSentence !== 'none' && (
                  <>
                    <h6 className="tra-text-web-heading-6 tw-font-normal tw-text-left tw-pb-6">
                      {loggedOutTitle}
                    </h6>
                    <p>{loggedOutSentence}</p>
                  </>
                )}
                <hr className="tw-hidden sm:tw-block tw-w-full tw-border-warm-200" />
                <CartBody isEmpty={isEmpty} outOfStockItems={outOfStockItems} />
              </div>
            </div>
          </>
        )}
      </div>
      {showRecommendations && !isAuthStep && <CartRecommendations />}
      {!isAuthStep && (
        <div className="md:tw-grid-in-c md:tw-sticky md:tw-w-auto tw-fixed tw-w-full tw-bottom-0 sm:tw-w-[600px]">
          <div
            className={classNames(
              'tw-z-10 tw-bg-white tw-relative',
              inDrawer && 'tw-shadow-fixedHeader',
            )}
          >
            <div className="tw-mx-auto tw-max-w-3xl tw-p-6 sm:tw-px-16">
              <CartFooter
                isLoading={isLoading}
                isEmpty={isEmpty}
                disabled={disabled}
                loadingCheckout={isMutating}
                setIsAuthStep={setIsAuthStep}
                navigateToCheckout={navigateToCheckout}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
